<template>
  <DashboardLayout>

    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewApprovalAuthorities' }"
        class="mb-0 main-identifier fw-semibold text-decoration-none">
        Approval Authorities
      </router-link> <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Update Approval Authority</p>
    </div>

    <div class="d-flex justify-content-center">

      <div class="form-outer">
        <form @submit.prevent="handleSubmit">
          <div class="crud-form position-relative">

            <div class="form-inner">
              <div class="form-header d-flex justify-content-between align-items-center">
                <!-- <h4>Edit Approval Authority</h4> -->
                <h1 class="mb-0">Approval Authority</h1>
              </div>

              <div class="form-body">
                <div class="row mt-2 mb-3">
                  <div class="col-md-12 row form-group read-only-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label for="year"
                        class="form-label">Name</label></div>
                    <div class="col-md-9 input-right">
                      <input type="text" class="form-control view-only-input" v-model="currentApprovalName"
                        placeholder="" required>
                    </div>
                  </div>

                  <div class="col-md-12 row form-group read-only-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label
                        for="subsector" class="form-label">Description</label></div>
                    <div class="col-md-9 input-right">
                      <div class="col-md-12 input-right">
                        <input class="form-control" v-model="newDescription" id="description"
                          :placeholder="currentDescription !== null ? currentDescription : ''" required />
                      </div>
                    </div>
                  </div>

                  <div v-if="canEditUniquePosition"
                    class="col-md-12 row form-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label
                        for="subsector" class="form-label">No. of Unique Position</label></div>
                    <div class="col-md-9 input-right">
                      <input type="number" class="form-control" v-model="newPosition"
                        :placeholder="currentPosition !== null ? currentPosition : ''" required>
                    </div>
                  </div>

                  <div class="col-md-12 row form-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label
                        for="unlimitedApproval" class="form-label">Unlimited Approval</label></div>
                    <div class="col-md-9 input-right">
                      <label class="unlimited-toggle" for="unlimitedApproval">
                        <input type="checkbox" id="unlimitedApproval" v-model="newUnlimitedApproval"
                          @change="onUnlimitedApprovalChange">
                        <span class="unlimited-toggle-track"><span class="unlimited-toggle-thumb"></span></span>
                        <span class="unlimited-toggle-text">This approval authority has no approval limit</span>
                      </label>
                    </div>
                  </div>

                  <div v-if="!newUnlimitedApproval"
                    class="col-md-12 row form-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label
                        for="subsector" class="form-label">Approval Limit Per Quarter</label></div>
                    <div class="col-md-9 input-right">
                      <input type="number" class="form-control" v-model="newApprovalLimit"
                        :placeholder="currentApprovalLimit !== null ? currentApprovalLimit : ''" required>
                    </div>
                  </div>

                  <div v-if="!newUnlimitedApproval"
                    class="col-md-12 row form-group position-relative floating-label-group">
                    <div class="col-md-3 label-left d-flex justify-content-end align-items-center"><label
                        for="subsector" class="form-label">Approval Amount Per Quarter (₦)</label></div>
                    <div class="col-md-9 input-right">
                      <input type="text" class="form-control" :value="formatNumber(newApprovalAmount)"
                        inputmode="numeric" pattern="[0-9,]*" @keypress="onlyDigitsAndComma"
                        @input="onApprovalAmountInput"
                        :placeholder="formatNumber(currentApprovalAmount !== null ? currentApprovalAmount : '')"
                        required>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Hidden field to store version -->
              <input type="hidden" v-model="form.version" />
              <input type="hidden" v-model="form.createdDate" />
              <input type="hidden" v-model="form.lastModifiedDate" />
              <input type="hidden" v-model="form.createdBy" />
              <input type="hidden" v-model="form.lastModBy" />
              <input type="hidden" v-model="form.userId" />

              <div class="d-flex justify-content-center ">
                <button class="submit-btn" type="submit" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
                  {{ loading ? 'Submitting...' : 'Submit' }}
                </button>
              </div>
            </div>


          </div>
        </form>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import Swal from 'sweetalert2'
import Cookies from 'js-cookie';
import { useUserStore } from '@/stores/user'

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const route = useRoute()
const id = route.params.id
const userStore = useUserStore()

// Only a super admin (a user with no named role assigned) may edit the
// "No. of Unique Position" field - everyone else can only view it.
const allRoles = [
  'qssu',
  'dirOfBudget',
  'perSec',
  'commOfBAndP',
  'registryOfficer',
  'groupHead',
  'groupMember',
];
const rolesLoaded = ref(false)
const isSuperAdmin = computed(() => {
  const profileRoles = userStore.profile?.role || {}
  return Object.keys(profileRoles).every(r => !allRoles.includes(r) || profileRoles[r] === false)
})
const canEditUniquePosition = computed(() => rolesLoaded.value && isSuperAdmin.value)

// const id = ref('')
const currentApprovalName = ref('')
const currentDescription = ref('')
const currentPosition = ref(null)
const currentApprovalLimit = ref(null)
const currentApprovalAmount = ref(null)
const currentUnlimitedApproval = ref(false)
const form = reactive({
  version: '',
  createdDate: '',
  lastModifiedDate: '',
  createdBy: '',
  lastModBy: '',
  userId: ''
})

const newDescription = ref('')
const newPosition = ref('')
const newApprovalLimit = ref('')
const newApprovalAmount = ref('')
const newUnlimitedApproval = ref(false)
const loading = ref(false)

const fetchApprovalAuthority = async () => {
  const token = Cookies.get('auth_token');
  try {
    const response = await axios.get(`${root_api_url}/eli/app-auth/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const appAuthData = response.data
    currentApprovalName.value = appAuthData.name || ''
    currentDescription.value = appAuthData.description || ''
    currentPosition.value = appAuthData.uniquePositionInd ?? null
    currentApprovalLimit.value = appAuthData.limitPerQtr ?? null
    currentApprovalAmount.value = appAuthData.approvalAmtPerQtr ?? null
    currentUnlimitedApproval.value = appAuthData.unlimitedApprovalInd === 1
    form.version = appAuthData.version || 0
    form.createdDate = appAuthData.createdDate || ''
    form.lastModifiedDate = appAuthData.lastModifiedDate || ''
    form.createdBy = appAuthData.createdBy || ''
    form.lastModBy = appAuthData.lastModBy || ''
    form.userId = appAuthData.userId || ''
    // Clear new values (placeholder approach)
    newDescription.value = appAuthData.description || ''
    newPosition.value = appAuthData.uniquePositionInd ?? ''
    newApprovalLimit.value = appAuthData.limitPerQtr ?? ''
    newApprovalAmount.value = currentApprovalAmount.value ?? 0
    newUnlimitedApproval.value = currentUnlimitedApproval.value

    // console.log('Fetched Approval Authority:', appAuthData)
  } catch (error) {
    console.error('Failed to fetch approval authority:', error)
  }
}

const handleSubmit = async () => {
  const token = Cookies.get('auth_token');

  const iconHtmlSuccess = `<span class="iconify" data-icon="ci:circle-check" style="font-size: 100px; color: green;"></span>`
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  const description = newDescription.value !== ''
    ? newDescription.value
    : currentDescription.value
  const uniquePositionInd = canEditUniquePosition.value && newPosition.value !== ''
    ? Number(newPosition.value)
    : currentPosition.value
  const limitPerQtr = newApprovalLimit.value !== ''
    ? Number(newApprovalLimit.value)
    : currentApprovalLimit.value
  const approvalAmtPerQtr = newApprovalAmount.value !== ''
    ? Number(newApprovalAmount.value)
    : currentApprovalAmount.value
  const unlimitedApprovalInd = newUnlimitedApproval.value ? 1 : 0

  const ApprovalAuthorityData = {
    id: id,
    name: currentApprovalName.value,
    description,
    uniquePositionInd,
    limitPerQtr,
    approvalAmtPerQtr,
    unlimitedApprovalInd,
    version: form.version,
    createdDate: form.createdDate,
    lastModifiedDate: form.lastModifiedDate,
    createdBy: form.createdBy,
    lastModBy: form.lastModBy,
    userId: form.userId,
  };

  // console.log("what is in here", ApprovalAuthorityData)

  loading.value = true;

  try {
    const result = await axios.put(`${root_api_url}/eli/app-auth/${id}`, ApprovalAuthorityData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (result.status === 200) {
      Swal.fire({
        html: `${iconHtmlSuccess} <h1 style="font-weight:600;">Success</h1> <p>Approval Authority Updated Successfully</p> `,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
      newDescription.value = currentDescription.value || ''
      newPosition.value = currentPosition.value ?? ''
      newApprovalLimit.value = currentApprovalLimit.value ?? ''
      newApprovalAmount.value = currentApprovalAmount.value ?? 0
      newUnlimitedApproval.value = currentUnlimitedApproval.value

      await fetchApprovalAuthority();
    }
  } catch (error) {
    // Swal.fire({
    //   html: `${iconHtmlError}
    //   <h1 style="font-weight:600;">Error</h1>
    //   <p>Failed to update approval authority</p>`,
    //   showConfirmButton: true,
    //   showCloseButton: true,
    //   icon: undefined,
    // });
    // console.error('Submission error:', err)
    // alert('Failed to create MDA: ' + (err.response?.data?.message || err.message))

    if (error.response) {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Unexpected error occurred. Kindly contact administrator.'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    } else {
      Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      });
    }
  } finally {
    loading.value = false;
  }
}


onMounted(() => {
  fetchApprovalAuthority(),
    document.title = "Edit Approval Authority"

  if (userStore.profile?.role) {
    rolesLoaded.value = true
  } else {
    const stop = watch(
      () => userStore.profile?.role,
      (val) => {
        if (val) {
          rolesLoaded.value = true
          stop()
        }
      },
      { immediate: true }
    )
  }
})

function onUnlimitedApprovalChange() {
  if (newUnlimitedApproval.value) {
    newApprovalAmount.value = 0
    newApprovalLimit.value = 0
  }
}

function formatNumber(val) {
  // console.log('formatNumber called with:', Number(val || 0).toLocaleString());
  return Number(val || 0).toLocaleString();
}

/**
 * Block keys other than digits, comma, or navigation
 */
function onlyDigitsAndComma(e) {
  const k = e.key;
  if (
    (k >= '0' && k <= '9') ||
    k === ',' ||
    ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(k)
  ) return;
  e.preventDefault();
}

/**
 * Handle the “Amount Approved” input:
 * - strip non‑digits
 * - update the numeric model
 * - re‑format the visible field
 */
function onApprovalAmountInput(e) {
  const digits = e.target.value.replace(/[^\d]/g, '');
  newApprovalAmount.value = digits === '' ? 0 : Number(digits);
  e.target.value = formatNumber(newApprovalAmount.value);
}
</script>
<style scoped>
.unlimited-toggle {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin-bottom: 0;
  user-select: none;
}

.unlimited-toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.unlimited-toggle-track {
  position: relative;
  flex-shrink: 0;
  width: 46px;
  height: 24px;
  background-color: #ccc;
  border-radius: 999px;
  transition: background-color 0.2s ease-in-out;
}

.unlimited-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background-color: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease-in-out;
}

.unlimited-toggle input:checked + .unlimited-toggle-track {
  background-color: #4caf50;
}

.unlimited-toggle input:checked + .unlimited-toggle-track .unlimited-toggle-thumb {
  transform: translateX(22px);
}

.unlimited-toggle input:focus-visible + .unlimited-toggle-track {
  outline: 2px solid #4caf50;
  outline-offset: 2px;
}

.unlimited-toggle-text {
    color: var(--color);
  font-size: 0.95rem;
}

.view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
}
</style>
